/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, DestroyRef, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ButtonComponent, PropertiesTableComponent, PropertiesTableRowComponent, SpinnerComponent } from "typedb-cloud-framework";
import { Cluster } from "../../../../concept/cluster";
import { ApplicationState } from "../../../../service/application-state.service";
import { CloudApiBackend } from "../../../../service/backend/cloud-api-backend.service";
import { ClusterApi } from "../../../../service/cluster/cluster-api.service";
import { SpaceApi } from "../../../../service/space/space-api.service";
import { ResourceService } from "../../../../service/resource.service";
import { ResourceAvailability } from "../../../scaffold/page/page-scaffold.component";
import {
    BehaviorSubject, Observable, Subject, catchError, combineLatest, combineLatestWith, distinctUntilChanged, filter,
    first, interval, map, of, scan, shareReplay, skip, startWith, switchMap, takeUntil, tap
} from "rxjs";
import { AsyncPipe } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatTooltipModule } from "@angular/material/tooltip";


@Component({
    selector: "tp-server-logs-viewing-pane",
    templateUrl: "./server-logs-viewing-pane.component.html",
    styleUrls: ["./server-logs-viewing-pane.component.scss"],
    imports: [
        AsyncPipe, SpinnerComponent, MatTooltipModule,
    ]
})
export class ServerLogsViewingPaneComponent implements OnInit, OnDestroy {
    @ViewChild("logViewer") logViewerEl?: ElementRef<HTMLPreElement>;
    @Input({ required: true }) serverId!: number;
    readonly cluster$ = new BehaviorSubject<Cluster | null>(null);
    readonly server$ = this.cluster$.pipe(
        takeUntilDestroyed(),
        filter(cluster => !!cluster),
        map(cluster => cluster!),
        switchMap((cluster) => interval(5000).pipe(
            takeUntilDestroyed(this.destroyRef),
            startWith(-1),
            switchMap(() => this.clusterApi.listServers(cluster.uuid)),
            map((servers) => servers?.find(x => x.id === this.serverId)),
        )),
        shareReplay(1),
    );
    readonly logs$ = new BehaviorSubject<string[] | null>(null);
    private readonly unsub$ = new Subject<void>();
    readonly availability$: Observable<ResourceAvailability> = combineLatest([this.cluster$, this.server$]).pipe(
        map(([x, y]) => x && y ? "ready" : "loading"), catchError(() => of("failed" as const))
    );
    @Input({ required: true }) set isStreamingEnabled(x: boolean) { this.isStreamingEnabled$.next(x) }
    private readonly isStreamingEnabled$ = new BehaviorSubject<boolean>(true);

    constructor(
        private route: ActivatedRoute, private app: ApplicationState, private destroyRef: DestroyRef,
        private clusterApi: ClusterApi, private spaceApi: SpaceApi, private resourceService: ResourceService,
        private apiBackend: CloudApiBackend,
    ) {
    }

    ngOnInit() {
        this.initDataLoader();
        this.initLogsLoader();
        this.autoscrollLogViewer();

        this.apiBackend.onReconnect$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.initLogsLoader();
        });
    }

    ngOnDestroy() {
        this.unsub$.next();
    }

    private initDataLoader() {
        this.route.params.pipe(
            filter((params) => params["space-id"] && params["cluster-id"]),
            map((params) => ({ spaceId: params["space-id"] as string, clusterId: params["cluster-id"] as string })),
            distinctUntilChanged((a, b) => a.clusterId === b.clusterId && a.spaceId === b.spaceId),
            tap(() => { this.unsub$.next(); }),
            switchMap(({ spaceId, clusterId }) => {
                return this.spaceApi.getSpace({ spaceId, teamUuid: this.app.requireCurrentTeam().uuid }, of(undefined)).pipe(
                    first(),
                    map((space) => ({ space: space.initial!, clusterId })),
                );
            }),
            switchMap(({ space, clusterId }) => {
                return this.clusterApi.getCluster({ clusterId: clusterId, spaceUuid: space.uuid }, this.unsub$);
            }),
        ).subscribe({
            next: (res) => {
                this.resourceService.processResponse({ resource$: this.cluster$, res: res, unsub$: this.unsub$, onResync: () => this.initDataLoader() });
            },
            error: (err) => {
                this.cluster$.error(err);
            }
        });
    }

    private initLogsLoader() {
        this.logs$.next(null);
        combineLatest([this.cluster$, this.server$]).pipe(
            filter(([cluster, server]) => !!cluster && !!server),
            map(([cluster, server]) => [cluster!!, server!!] as const),
            distinctUntilChanged(([_, s1], [_0, s2]) => s1.uuid === s2.uuid),
            tap(() => this.unsub$.next()),
            switchMap(([cluster, server]) =>
                this.clusterApi.listServerLogs(
                    { clusterUuid: cluster.uuid, serverUuid: server.uuid, serverId: server.id, initialLineCount: 100 }, this.unsub$
                ).pipe(
                    scan(((current, next) => {
                        const newArray = current.slice();
                        next.lines.forEach((line, idx) => newArray[next.lineNumberStart + idx] = line);
                        return newArray;
                    }), [] as string[]),
                    combineLatestWith(this.isStreamingEnabled$)
                )
            ),
            takeUntil(this.unsub$.pipe(skip(1))),
        ).subscribe(([logs, isStreamingEnabled]) => {
            if (isStreamingEnabled) this.logs$.next(logs);
        });
    }

    autoscrollLogViewer() {
        this.logs$.subscribe(_ => {
            if (this.logViewerEl) {
                this.logViewerEl.nativeElement.scrollTop = this.logViewerEl.nativeElement.scrollHeight;
            }
        });
    }
}
